import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./base.query";
import { InitializeInterviewResponse } from "../../types/interview.type";
import {
 GetInterviewsResponse,
 PositionInterviewsResponse,
 ReInterviewRequest,
 ReInterviewResponse,
 SearchJobsForInterviewResponse,
} from "../../types/interview.v2.type";

export enum InterviewTags {
 SET_UP_INTERVIEW = "set_up_interview",
 REPLAY_MESSAGE = "replay_message",
 GENERATE_SPEECH = "generate_speech",
 GET_INTERVIEW_LIST = "get_interview_list",
 INTERVIEW = "Interview",
 JOB_SEARCH = "JobSearch",
 GET_INTERVIEWS = "GetInterviews",
 SEARCH_JOBS_FOR_INTERVIEW = "SearchJobsForInterview",
}

export const interviewApi = createApi({
 reducerPath: "interviewApi",
 baseQuery: baseQueryWithReauth,
 tagTypes: [...Object.values(InterviewTags)],
 endpoints: (builder) => ({
  // The original "Initialize" endpoint. Keep if you still use it:
  setUpInterview: builder.mutation<any, any>({
   query: (interviewData) => ({
    url: "Interview/Initialize",
    method: "POST",
    body: interviewData,
   }),
  }),

  // 1) The "Reply" endpoint => returns { response, isProvidingScore, ... }
  replayMessage: builder.mutation<
   InitializeInterviewResponse,
   { threadId: string; message: string }
  >({
   query: ({ threadId, message }) => {
    return {
     url: "Interview/Reply",
     method: "POST",
     body: {
      threadId: threadId,
      CandidateAnswer: message,
     },
    };
   },
  }),

  // 2) The "GenerateSpeech" endpoint => returns a Blob (MP3)
  generateSpeach: builder.mutation<
   Blob, // The mutation result type
   { voiceName: string; message: string }
  >({
   query: ({ voiceName, message }) => ({
    url: "Interview/GenerateSpeech",
    method: "POST",

    // By default, fetchBaseQuery expects JSON, so let's override
    // the response to be a Blob:
    responseHandler: async (response) => response.blob(),
    body: {
     TextMessage: message,
     Voice: voiceName,
    },
   }),
  }),
  // 3) The "GetInterviewScore" endpoint => returns a number
  getInterviewList: builder.query<
   { interviews: any[]; totalCount: number },
   string
  >({
   query: (params) => ({
    url: `Interview/GetInterviews?${params}`,
    method: "GET",
   }),
   providesTags: [InterviewTags.GET_INTERVIEW_LIST],
  }),
  reInterview: builder.mutation<ReInterviewResponse, ReInterviewRequest>({
   query: (body) => ({
    url: "Interview/ReInterview",
    method: "POST",
    body,
   }),
   invalidatesTags: [InterviewTags.INTERVIEW],
  }),

  // GET - GetInterviews V2
  getInterviews: builder.query<
   GetInterviewsResponse,
   { pageIndex: number; search?: string | null }
  >({
   query: ({ pageIndex, search }) => ({
    url: `Interview/GetInterviewedPositions?pageIndex=${pageIndex}&search=${search}`,
   }),
   providesTags: [InterviewTags.INTERVIEW],
  }),

  // GET - SearchJobsForInterview V2
  searchJobsForInterview: builder.query<
   SearchJobsForInterviewResponse,
   { searchKey: string; userId: string }
  >({
   query: ({ searchKey, userId }) =>
    searchKey
     ? `Job/SearchJobsForInterview?searchkey=${encodeURIComponent(
        searchKey
       )}&userId=${userId}`
     : "Job/SearchJobsForInterview",
   providesTags: [InterviewTags.SEARCH_JOBS_FOR_INTERVIEW],
  }),

  //  GET - Position Interviews V2
  getPositionInterviews: builder.query<
   PositionInterviewsResponse,
   { positionId: number }
  >({
   query: ({ positionId }) => ({
    url: `Interview/GetInterviewsByPosition?positionId=${positionId}`,
    method: "GET",
   }),
   providesTags: (result, error, { positionId }) =>
    result
     ? [{ type: InterviewTags.INTERVIEW, id: positionId }]
     : [{ type: InterviewTags.INTERVIEW, id: "LIST" }],
  }),
 }),
});

export const {
 useSetUpInterviewMutation,
 useReplayMessageMutation,
 useGenerateSpeachMutation,
 useGetInterviewListQuery,
 useReInterviewMutation,
 useGetInterviewsQuery,
 useSearchJobsForInterviewQuery,
 useGetPositionInterviewsQuery,
} = interviewApi;

export default interviewApi;
